import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.css';
import { applyWhiteLabelCssVars } from './config/whiteLabel';

// #1198 — OEM primary color token when white-label is enabled.
try {
  applyWhiteLabelCssVars();
} catch {
  // Brand CSS must never prevent React from mounting.
}

// #988: defer Sentry off the homepage critical path. Eager init + static
// `@sentry/react` forced ~254KB vendor-sentry into every route's initial
// graph (Layout→ErrorBoundary→main). Idle/load-schedule a dynamic import so
// marketing LCP is not blocked; errors before init are simply unreported.
//
// Marketing routes wait ~25s (not idle@4s): Lighthouse unused-javascript still
// downloaded `sdk-*.js` (~42KiB Sentry) when init ran inside the audit window.
const MARKETING_SENTRY_DELAY_MS = 25_000;

function isMarketingPath(pathname: string): boolean {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path === '/') return true;
  return [
    '/products',
    '/industries',
    '/about',
    '/foam-guide',
    '/tutorial',
    '/contact',
    '/faq',
    '/gallery',
    '/premade-cases',
    '/compare',
    '/pro-design',
    '/blog',
    '/shop',
  ].some((p) => path === p || path.startsWith(`${p}/`));
}

function scheduleSentryInit(): void {
  let started = false;
  const run = () => {
    if (started) return;
    started = true;
    void import('./utils/sentry').then(({ initSentry }) => {
      initSentry();
    });
  };
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };

  if (isMarketingPath(w.location.pathname)) {
    // #988: stay quiet during PSI/Lighthouse, but start ASAP when the user
    // SPA-navigates into the design/editor funnel (Bugbot medium on #1162).
    const onMaybeLeaveMarketing = () => {
      if (!isMarketingPath(w.location.pathname)) run();
    };
    const origPush = history.pushState.bind(history);
    const origReplace = history.replaceState.bind(history);
    history.pushState = ((...args: Parameters<History['pushState']>) => {
      origPush(...args);
      onMaybeLeaveMarketing();
    }) as History['pushState'];
    history.replaceState = ((...args: Parameters<History['replaceState']>) => {
      origReplace(...args);
      onMaybeLeaveMarketing();
    }) as History['replaceState'];
    w.addEventListener('popstate', onMaybeLeaveMarketing);
    w.setTimeout(run, MARKETING_SENTRY_DELAY_MS);
    return;
  }

  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(() => run(), { timeout: 4000 });
  } else {
    w.addEventListener('load', () => {
      w.setTimeout(run, 1);
    });
  }
}
scheduleSentryInit();

// GH#914: a deploy removes the chunks an already-open tab still references, so
// the next lazy import dead-ends the app. Reload once to pick up the build that
// IS on the server. The marker is cleared by StalePreloadHealthBeacon, which
// mounts inside <Suspense> once a route chunk actually resolves — clearing here
// would clear while the chunk is still in flight and reopen the reload loop.
import { registerStalePreloadRecovery } from './utils/stalePreloadRecovery';
import { captureAttribution } from './utils/attribution';
registerStalePreloadRecovery({
  target: window,
  storage: window.sessionStorage,
  reload: () => window.location.reload(),
  // GH#954: the recovery stands aside for two different reasons and the data
  // could not tell them apart. `already-reloaded` is the #914 trade-off working
  // as designed; `storage-unavailable` means no reload EVER happened and that
  // user got the pre-#914 experience. Counting the second is the point.
  // #988: breadcrumb via dynamic import so @sentry/react stays off the initial graph.
  onSkip: (reason) => {
    void import('@sentry/react').then((Sentry) => {
      Sentry.addBreadcrumb({
        category: 'client.preload.recovery.skipped',
        message: `[PRELOAD:SKIP] ${reason}`,
        level: reason === 'storage-unavailable' ? 'warning' : 'info',
        data: { reason, timestamp: new Date().toISOString() },
      });
    });
  },
});

// First-touch marketing attribution: record utm_* + referrer from the landing
// URL before the router strips or rewrites anything. Idempotent per session.
captureAttribution(window.location, document.referrer);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <App />
    </BrowserRouter>
  </React.StrictMode>
);
